import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  shortDiceRushMultipliersSchema,
  longDiceRushMultipliersSchema,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { classifyReturn } from './inheritance.js';
import { prettyGame } from '../games.js';

export interface DiceRushMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** Wire shape shared by both tables; only the bet-type prefix differs. */
interface DiceRushMultipliersValue {
  version: number;
  returns: Record<string, number>;
}

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10000;

/**
 * A rate keeps `floor(stake × bps / 10000)` exact on the 25-credit stake grid
 * only when `gcd(bps, 10000) >= 400`, i.e. when bps is a multiple of 400. Off
 * it, every win is floored down. Mirrors STAKE_GRID_CREDITS in the game packages.
 */
const GRID_BPS = 400;

type Family = 'TOTAL' | 'ANY' | 'DOUBLE';

/**
 * Winning rolls out of 6^3 = 216 per family. Mirrors WINNING_OUTCOMES in
 * @apx/game-shortdicerush / @apx/game-longdicerush. The dice are uniform and
 * not admin-tunable, so these are constants and each cell's RTP is exact:
 * `wins / 216 × returnBps / 10000`.
 */
const WINS: Record<Family, number> = { TOTAL: 108, ANY: 91, DOUBLE: 16 };
const ROLLS = 216;

/**
 * Built-in total-return bps per family — mirrors DEFAULT_TOTAL/ANY/DOUBLE_RETURN_BPS
 * in both game packages. Guarded by `multipliers-defaults.test.ts`.
 */
const FAMILY_DEFAULT_BPS: Record<Family, number> = {
  TOTAL: 19_200,
  ANY: 22_800,
  DOUBLE: 129_600,
};

interface Cell {
  /** Bet type without its game prefix, e.g. `ANY_3`. */
  suffix: string;
  family: Family;
  label: string;
}

const FACES = [1, 2, 3, 4, 5, 6] as const;

const CELLS: Cell[] = [
  { suffix: 'SMALL', family: 'TOTAL', label: 'SMALL (3-10)' },
  { suffix: 'BIG', family: 'TOTAL', label: 'BIG (11-18)' },
  { suffix: 'ODD', family: 'TOTAL', label: 'ODD' },
  { suffix: 'EVEN', family: 'TOTAL', label: 'EVEN' },
  ...FACES.map((n): Cell => ({ suffix: `ANY_${n}`, family: 'ANY', label: `Any Dice ${n}` })),
  ...FACES.map((n): Cell => ({ suffix: `DOUBLE_${n}`, family: 'DOUBLE', label: `Double Match ${n}·${n}` })),
];

const FAMILY_NOTE: Record<Family, string> = {
  TOTAL: 'Wins on 108 of 216 rolls.',
  ANY: 'Wins on 91 of 216 rolls — one, two or three matching dice pay the same.',
  DOUBLE: 'Wins on 16 of 216 rolls — a pair or a triple of the face.',
};

function prefixFor(gameId: string): 'SDR' | 'LDR' {
  return gameId === 'longdicerush' ? 'LDR' : 'SDR';
}

/** Built-in total-return bps for every bet type of this table, keyed by wire code. */
export function diceRushDefaultReturns(gameId: string): Record<string, number> {
  const prefix = prefixFor(gameId);
  return Object.fromEntries(CELLS.map((c) => [`${prefix}_${c.suffix}`, FAMILY_DEFAULT_BPS[c.family]]));
}

function cellRtp(family: Family, returnBps: number): number {
  return (WINS[family] / ROLLS) * (returnBps / BPS_SCALE);
}

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/** Human hint: total-return bps → "1.92× return (0.92:1)". */
function returnHint(bps: number): string {
  const ret = bps / BPS_SCALE;
  const odds = (bps - BPS_SCALE) / BPS_SCALE;
  return `${ret.toFixed(2)}× return (${odds.toFixed(2)}:1)`;
}

/**
 * Dice Rush payout-multiplier editor, shared by Short and Long Dice Rush. One
 * total-return-bps input per bet area (`returns`): 19200 = 1.92× return.
 *
 * These multipliers are the game's ONLY tunable RTP surface — three fair dice,
 * no RNG-weight lever — so each area's RTP is exact and shown live as you type.
 *
 * In integrator scope: rows show inherited/override tags and per-row revert, and
 * readForm() submits only the override keys. In provider scope: the full table,
 * no inheritance UI. Changes ride the two-admin approval flow.
 */
export async function renderDiceRushMultipliersPage(
  root: HTMLElement,
  deps: DiceRushMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;
  const prefix = prefixFor(deps.gameId);
  const schema = prefix === 'LDR' ? longDiceRushMultipliersSchema : shortDiceRushMultipliersSchema;
  const defaults = diceRushDefaultReturns(deps.gameId);
  const keyOf = (c: Cell): string => `${prefix}_${c.suffix}`;
  const familyOf = new Map(CELLS.map((c) => [keyOf(c), c.family]));
  const keys = CELLS.map(keyOf);
  const defaultValue: DiceRushMultipliersValue = { version: 1, returns: { ...defaults } };

  function rowHtml(c: Cell, first: boolean): string {
    const key = keyOf(c);
    const tagCell = isIntegratorScope
      ? `<td data-tag="${key}" class="muted" style="min-width:80px">—</td>`
      : `<td class="muted">default ${defaults[key]} bps</td>`;
    const revertCell = isIntegratorScope
      ? `<td><button data-revert="${key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
      : `<td></td>`;
    const heading = first
      ? `<tr><td colspan="7" class="muted" style="padding-top:14px;font-size:12px">${FAMILY_NOTE[c.family]}</td></tr>`
      : '';
    return `${heading}<tr data-row="${key}" data-source="inherited">
      <td><strong>${c.label}</strong><div class="muted" style="font-size:12px"><code>${key}</code></div></td>
      <td><input data-bet="${key}" type="number" min="0" max="100000000" step="400" style="width:110px" /></td>
      <td class="muted" data-hint="${key}">—</td>
      <td class="muted" style="white-space:nowrap">${((WINS[c.family] / ROLLS) * 100).toFixed(4)}%</td>
      <td data-rtp="${key}" style="white-space:nowrap">—</td>
      ${tagCell}
      ${revertCell}
    </tr>`;
  }

  const resetAllBtn = isIntegratorScope
    ? `<button id="drm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>19200 = 1.92× return</code>. Keep every rate a multiple of ${GRID_BPS} — anything else takes wins off the 25-credit stake grid and floors them down. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Per-area return (bps)</h2>
      <table>
        <thead><tr><th>Bet area</th><th>Return (bps)</th><th>Multiple</th><th>P(win)</th><th>RTP</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="drm-rows">${CELLS.map((c, i) => rowHtml(c, i === 0 || CELLS[i - 1]!.family !== c.family)).join('')}</tbody>
      </table>
      <p class="muted" id="drm-headline" style="margin-top:10px">—</p>
      <div class="row" style="margin-top:14px">
        <button id="drm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="drm-save" class="primary">Propose change</button>
        <span id="drm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="drm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#drm-rows')!;
  const headlineEl = root.querySelector<HTMLElement>('#drm-headline')!;
  const msgEl = root.querySelector<HTMLElement>('#drm-msg')!;

  // Per-row inherited values for revert (integrator scope only).
  const inheritedValues = new Map<string, number>();

  const inputs = new Map<string, HTMLInputElement>();
  for (const key of keys) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) inputs.set(key, el);
  }

  function refreshDerived(): void {
    let bestKey = keys[0]!;
    let best = -Infinity;
    for (const key of keys) {
      const bps = Number(inputs.get(key)?.value) || 0;
      const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
      if (hintEl) {
        const offGrid = bps % GRID_BPS !== 0
          ? ` <span style="color:var(--error)">off the 25-credit grid — wins round down</span>`
          : '';
        hintEl.innerHTML = `${returnHint(bps)}${offGrid}`;
      }
      const rtp = cellRtp(familyOf.get(key)!, bps);
      const rtpEl = rowsEl.querySelector<HTMLElement>(`[data-rtp="${key}"]`);
      if (rtpEl) {
        const over = rtp > 1 ? ' <span style="color:var(--error)">over 100%!</span>' : '';
        rtpEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(2)}%</strong>${over}`;
      }
      if (rtp > best) {
        best = rtp;
        bestKey = key;
      }
    }
    // Headline is the BEST area on the board, matching the RTP hero above
    // (admin-api takes the max across bet types, not an average).
    headlineEl.innerHTML =
      `Headline RTP (best area on the board): <strong>${CELLS.find((c) => keyOf(c) === bestKey)!.label}</strong> at ` +
      `<strong style="color:${rtpColor(best)}">${(best * 100).toFixed(2)}%</strong> — house edge ` +
      `<strong>${((1 - best) * 100).toFixed(2)}%</strong>.`;
  }

  function markRow(key: string, source: 'override' | 'inherited'): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    const isOverride = source === 'override';
    if (row) row.dataset.source = source;
    if (tagEl) {
      tagEl.textContent = source;
      tagEl.style.color = isOverride ? 'var(--accent)' : 'var(--text-dim)';
      tagEl.style.fontWeight = isOverride ? '600' : '';
    }
    if (revertEl) revertEl.style.display = isOverride ? '' : 'none';
  }

  /** Seed rows from a full value (provider scope or reset). */
  function fillForm(value: DiceRushMultipliersValue): void {
    for (const [key, input] of inputs) input.value = String(value.returns[key] ?? defaults[key]);
    refreshDerived();
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, defaults[key]!);
      input.value = String(classified.value);
      inheritedValues.set(
        key,
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : defaults[key]!,
      );
      markRow(key, classified.source);
    }
    refreshDerived();
  }

  /** Build the propose payload. In integrator scope, only the override keys. */
  function readForm(): DiceRushMultipliersValue {
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) {
      const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
      if (deps.isProviderScope || row?.dataset.source === 'override') {
        returns[key] = Math.round(Number(input.value) || 0);
      }
    }
    return { version: 1, returns };
  }

  for (const [key, input] of inputs) {
    input.addEventListener('input', () => {
      if (isIntegratorScope) markRow(key, 'override');
      refreshDerived();
    });
  }

  if (isIntegratorScope) {
    for (const key of keys) {
      rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`)?.addEventListener('click', () => {
        const input = inputs.get(key);
        if (input) input.value = String(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
        refreshDerived();
      });
    }

    root.querySelector<HTMLButtonElement>('#drm-reset-all')?.addEventListener('click', () => {
      for (const [key, input] of inputs) {
        input.value = String(inheritedValues.get(key) ?? defaults[key]);
        markRow(key, 'inherited');
      }
      refreshDerived();
      msgEl.textContent = 'All areas reset to provider/baked defaults (not saved).';
      msgEl.className = 'muted';
    });
  }

  /** Re-read the surface after a save/approve, respecting the current scope. */
  async function reload(): Promise<void> {
    if (isIntegratorScope) {
      const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
      const own = inh.ownOverride.multipliers as Partial<DiceRushMultipliersValue> | undefined;
      const prov = inh.providerDefault.multipliers as Partial<DiceRushMultipliersValue> | undefined;
      fillFormInherited(own?.returns, prov?.returns);
      return;
    }
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = value as Partial<DiceRushMultipliersValue> | null;
    fillForm(stored?.returns ? { version: 1, returns: stored.returns } : defaultValue);
  }

  await reload();

  root.querySelector<HTMLButtonElement>('#drm-reset')!.addEventListener('click', () => {
    fillForm(defaultValue);
    if (isIntegratorScope) for (const key of keys) markRow(key, 'override');
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#drm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = schema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#drm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
